import { useState, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  ASSISTANT_PANEL_MAX_WIDTH,
  ASSISTANT_PANEL_MIN_WIDTH,
  clampAssistantPanelWidth,
} from '../helpers/panel-width';
import { cn } from '@src/design-system/helpers';

export function AssistantResizeHandle({
  width,
  onWidthChange,
  onReset,
}: {
  width: number;
  onWidthChange: (width: number) => void;
  onReset: () => void;
}) {
  const [resizing, setResizing] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [cursor, setCursor] = useState({ x: 0, y: 0 });

  const showRail = hovered || resizing;
  const showTip = hovered && !resizing;

  const onPointerDown = (event: ReactPointerEvent<HTMLButtonElement>) => {
    if (event.button !== 0) return;
    event.preventDefault();
    const handle = event.currentTarget;
    const startX = event.clientX;
    const startWidth = width;
    setResizing(true);
    try {
      handle.setPointerCapture(event.pointerId);
    } catch {
      // jsdom may not implement pointer capture.
    }
    document.body.style.cursor = 'ew-resize';
    document.body.style.userSelect = 'none';

    const onPointerMove = (move: PointerEvent) => {
      // Panel is docked on the right: dragging the handle left widens it.
      onWidthChange(
        clampAssistantPanelWidth(startWidth + (startX - move.clientX))
      );
    };
    const onPointerUp = () => {
      setResizing(false);
      try {
        handle.releasePointerCapture(event.pointerId);
      } catch {
        // jsdom may not implement pointer capture.
      }
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      window.removeEventListener('pointermove', onPointerMove);
      window.removeEventListener('pointerup', onPointerUp);
    };
    window.addEventListener('pointermove', onPointerMove);
    window.addEventListener('pointerup', onPointerUp);
  };

  return (
    <>
      <button
        type='button'
        data-slot='assistant-resize-handle'
        aria-label='Resize assistant panel'
        aria-orientation='vertical'
        aria-valuemin={ASSISTANT_PANEL_MIN_WIDTH}
        aria-valuemax={ASSISTANT_PANEL_MAX_WIDTH}
        aria-valuenow={Math.round(width)}
        data-resizing={resizing ? 'true' : undefined}
        onPointerDown={onPointerDown}
        onDoubleClick={(event) => {
          event.preventDefault();
          onReset();
        }}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        onMouseMove={(event) => {
          setCursor({ x: event.clientX, y: event.clientY });
        }}
        className='absolute inset-y-4 start-0 z-20 flex w-4 -translate-x-1/2 left-2 cursor-ew-resize items-center justify-center'
      >
        {/*
          Nested: outer = 2px primary-100 ring (padding), inner = 3px
          primary-1000 line. Shown only while hovering / resizing.
        */}
        <div
          aria-hidden='true'
          className={cn(
            'pointer-events-none absolute inset-y-0 left-1/2 -translate-x-1/2 rounded-full bg-primary-200 p-0.5 transition-opacity',
            showRail ? 'opacity-100' : 'opacity-0'
          )}
        >
          <div className='h-full w-[3px] rounded-full bg-primary-1000' />
        </div>
      </button>

      {showTip &&
        createPortal(
          <div
            role='tooltip'
            className='pointer-events-none fixed z-50 max-w-sm rounded-md border border-gray-100 bg-white px-3 py-2 text-sm font-medium leading-[1.5] text-gray-900 shadow-[1px]'
            style={{
              left: cursor.x,
              top: cursor.y,
              transform: 'translate(-50%, calc(-100% - 12px))',
            }}
          >
            <p>
              Close <span className='text-gray-600'>Esc</span>
              <br />
              Resize <span className='text-gray-600'>Drag</span>
              <br />
              Reset <span className='text-gray-600'>Double Click</span>
            </p>
          </div>,
          document.body
        )}
    </>
  );
}
